"use client";

import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import CustomColorPicker from "./CustomColorPicker";
import { useNewPropertyState2 } from "@/components/RightStyle/usePropertyStateV2";
import { PropertyType } from "@/tailwind/tailwindUtils";
import type { ComboBoxOptionType } from ".";
import type { GradientState } from "../../hooks/useGradient";
import {
  cleanGradientVariables,
  parseColorValue,
} from "./color-picker/utils/colorFormatUtils";

interface GradientColorPickerProps {
  classList: string[];
  gradient: GradientState | null;
  setGradient: (val: React.SetStateAction<GradientState | null>) => void;
  instantUpdateValue: (option: ComboBoxOptionType) => void;
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: string;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const GradientColorPicker: React.FC<GradientColorPickerProps> = ({
  classList,
  gradient,
  setGradient,
  instantUpdateValue,
  pickTailwindClassForProperty,
}) => {
  const [localGradient, setLocalGradient] = useState<GradientState | null>(
    gradient,
  );

  const fromColorValue = useMemo(() => {
    if (localGradient?.from?.color?.value) {
      const cleanedValue = cleanGradientVariables(
        localGradient.from.color.value,
      );
      const parsedColor = parseColorValue(cleanedValue);
      return parsedColor.hex;
    }
    return "#ffffff";
  }, [localGradient]);

  const handleUpdateGradientSet = useCallback(
    (value: GradientState) => {
      setLocalGradient(value);
      setGradient(value);
    },
    [setGradient],
  );

  useEffect(() => {
    if (localGradient == null || localGradient.direction == null) {
      setLocalGradient(gradient);
    }
  }, [gradient, localGradient]);

  const fromColorState = useNewPropertyState2({
    elementClasses: classList,
    tailwindClassMap: {},
    propertyType: PropertyType.FROM_COLOR,
    cssValues: {},
  });

  const handleFromColorPickerSetValue = (option: ComboBoxOptionType) => {
    pickTailwindClassForProperty({
      option,
      propertyType: PropertyType.FROM_COLOR,
      setTemporaryDraggingValue: fromColorState.setTemporaryDraggingValue,
    });
  };

  return (
    <div className="col-span-14" style={{ position: "relative" }}>
      <CustomColorPicker
        showHex={false}
        propertyState={fromColorState}
        buildArbitraryClass={(input: string) => ({
          tailwind: `from-[${input}]`,
          css: `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`,
        })}
        instantUpdateValue={(option) => {
          instantUpdateValue(option);
        }}
        setValue={handleFromColorPickerSetValue}
        comboboxOptions={[]}
        tooltipContent="From color"
        onClear={() => {}}
      />
    </div>
  );
};

export default GradientColorPicker;
